import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type DocsUser = { sub?: string; name?: string; email?: string };

type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: DocsUser };

const SessionCtx = createContext<{
  session: SessionState;
  refresh: () => Promise<void>;
} | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) {
        setSession({ status: 'anonymous' });
        return;
      }
      const data = await res.json();
      if (data.authenticated && data.user) {
        setSession({ status: 'authenticated', user: data.user });
      } else {
        setSession({ status: 'anonymous' });
      }
    } catch {
      setSession({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionCtx.Provider value={{ session, refresh }}>
      {children}
    </SessionCtx.Provider>
  );
}

function useSessionContext() {
  const v = useContext(SessionCtx);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}

export function useSession() {
  const { session, refresh } = useSessionContext();
  const loading = session.status === 'loading';

  const user = session.status === 'authenticated' ? session.user : null;

  async function logoutAndRefresh() {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    await refresh();
  }

  return { loading, user, refresh, logoutAndRefresh };
}
